import React, { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { errMsg } from '@/services/staffAttendanceApi';
import { ptApi } from '@/services/ptFilesApi';

const CodeRow = ({ kind, label, value, tid, onSaved, missing }) => {
  const [v, setV] = useState(value || '');
  const [busy, setBusy] = useState(false);
  useEffect(() => setV(value || ''), [value]);
  const save = async () => {
    if (v.trim() === (value || '')) return;
    setBusy(true);
    try { await ptApi.saveCode(kind, label, v.trim()); toast.success(v.trim() ? `${label} → ${v.trim()}` : `${label} removed`); onSaved(); } catch (e) { toast.error(errMsg(e, 'Could not save')); } finally { setBusy(false); }
  };
  return (
    <div className={`flex items-center gap-2 rounded-md px-2 py-1 ${missing ? 'bg-rose-50' : ''}`} data-testid={tid}>
      <span className="flex-1 truncate text-sm" title={label}>{label}{missing && <span className="ml-1 text-xs text-rose-600">missing</span>}</span>
      <Input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && save()} className={kind === 'hsn' ? 'w-[220px] h-8' : 'w-[110px] h-8'} data-testid={`${tid}-input`} />
      <Button size="sm" variant="ghost" disabled={busy || v.trim() === (value || '')} onClick={save} data-testid={`${tid}-save`}><Save className="w-4 h-4" /></Button>
    </div>
  );
};

const slug = (s) => s.replace(/[^A-Za-z0-9]+/g, '-');

const Section = ({ kind, title, rows, missing, q, onSaved }) => {
  const have = new Set(rows.map((r) => r.label.toUpperCase()));
  const list = [...missing.filter((m) => !have.has(m.toUpperCase())).map((m) => ({ label: m, value: '', missing: true })), ...rows]
    .filter((r) => !q || r.label.toLowerCase().includes(q.toLowerCase()) || String(r.value).includes(q));
  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title} ({rows.length})</div>
      {list.length === 0 ? <p className="text-xs text-gray-400">None yet — read an old PT file from a distributor's PT codes</p>
        : list.map((r) => <CodeRow key={r.label} kind={kind} label={r.label} value={r.value} missing={r.missing} onSaved={onSaved} tid={`pt-code-${kind}-${slug(r.label)}`} />)}
    </div>
  );
};

/** Design → Shoper code and HSN → Shoper tax-group name, shared by every distributor. Missing ones from a blocked invoice come first. */
export const PtDesignCodesDialog = ({ missing, onClose, onSaved }) => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const load = () => ptApi.codes().then(setData).catch((e) => toast.error(errMsg(e, 'Could not load the codes')));
  useEffect(() => { if (missing) { setQ(''); load(); } }, [missing]); // eslint-disable-line react-hooks/exhaustive-deps
  const saved = () => { load(); onSaved(); };
  return (
    <Dialog open={!!missing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="pt-design-dialog">
        <DialogHeader>
          <DialogTitle>Design Shoper codes</DialogTitle>
          <DialogDescription>Same for every distributor. Type a code and press Enter; clear it to remove.</DialogDescription>
        </DialogHeader>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search design, HSN or code" data-testid="pt-design-search" />
        {!data ? <Loader2 className="w-6 h-6 mx-auto my-8 animate-spin text-amber-600" /> : (
          <div className="space-y-4">
            <Section kind="design" title="Designs" rows={data.designs} missing={missing?.designs || []} q={q} onSaved={saved} />
            <Section kind="hsn" title="HSN names" rows={data.hsn} missing={missing?.hsn || []} q={q} onSaved={saved} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
